import { useState, useEffect } from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import ConsumerApp from './apps/ConsumerApp';
import RetailerApp from './apps/RetailerApp';
import AdminApp from './apps/AdminApp';
import CharityApp from './apps/CharityApp';
import Landing from './pages/Landing';
import PasswordReset from './components/PasswordReset';
import StoreDirectory from './components/consumer/StoreDirectory';
import StoreStorefront from './components/consumer/StoreStorefront';
import GrocersDirectory from './pages/GrocersDirectory';
import RetailerStorefront from './pages/RetailerStorefront';
import SmsOptInPage from './pages/SmsOptInPage';
import PressRelease from './pages/PressRelease';
import LegalPage from './components/legal/LegalPage';
import LegalAcceptanceGate from './components/legal/LegalAcceptanceGate';
import { LEGAL_DOCS } from './legal';
import { Toaster } from '@/components/ui/sonner';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { isRetailerSubdomain, getRetailerSlug } from './utils/subdomain';

// Thin route wrapper that looks up the legal doc by id and renders LegalPage.
// Kept as a tiny inline component to avoid spawning 7 nearly-identical files.
function LegalRoute({ id }) {
  const doc = LEGAL_DOCS[id];
  if (!doc) return <Navigate to="/grocers" replace />;
  return <LegalPage doc={doc} />;
}

// RTL wrapper that applies direction based on language
function RTLWrapper({ children }) {
  const { isRTL, locale } = useLanguage();
  const rtl = isRTL();
  
  // Apply RTL direction to document
  useEffect(() => {
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    document.documentElement.lang = locale;
    
    // Add/remove RTL class for additional styling
    if (rtl) {
      document.body.classList.add('rtl');
    } else {
      document.body.classList.remove('rtl');
    }
  }, [rtl, locale]);

  return (
    <div className="App" dir={rtl ? 'rtl' : 'ltr'}>
      {children}
    </div>
  );
}

function App() {
  // Get user from localStorage for store pages
  const getUser = () => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  return (
    <LanguageProvider>
      <RTLWrapper>
        <BrowserRouter>
          {isRetailerSubdomain() ? (
            /* Retailer subdomain: walmart.dealshaq.com → RetailerApp */
            <Routes>
              <Route path="/*" element={<RetailerApp subdomainSlug={getRetailerSlug()} />} />
            </Routes>
          ) : (
            <Routes>
              {/* Root redirect to grocers directory */}
              <Route path="/" element={<Navigate to="/grocers" replace />} />
              <Route path="/reset-password" element={<PasswordReset />} />
              {/* Public legal pages — shared across consumer + retailer + public */}
              <Route path="/terms" element={<LegalRoute id="consumer-terms" />} />
              <Route path="/privacy" element={<LegalRoute id="consumer-privacy" />} />
              <Route path="/cookies" element={<LegalRoute id="cookie-policy" />} />
              <Route path="/refund-policy" element={<LegalRoute id="refund-recall" />} />
              <Route path="/retailer-agreement" element={<LegalRoute id="retailer-agreement" />} />
              <Route path="/retailer-pilot-agreement" element={<LegalRoute id="retailer-pilot" />} />
              <Route path="/consumer-pilot-agreement" element={<LegalRoute id="consumer-pilot" />} />
              {/* SMS opt-in — Twilio TFN compliance public page */}
              <Route path="/sms-optin" element={<SmsOptInPage />} />
              {/* Public newsroom / press release */}
              <Route path="/press" element={<PressRelease />} />
              <Route path="/news" element={<PressRelease />} />
              {/* Consumer-facing public pages */}
              <Route path="/grocers" element={<GrocersDirectory />} />
              <Route path="/consumer/*" element={<ConsumerApp />} />
              {/* Retailer portal */}
              <Route path="/retailer/*" element={<RetailerApp />} />
              {/* Admin portal */}
              <Route path="/admin/*" element={<AdminApp />} />
              {/* Charity portal */}
              <Route path="/charity/*" element={<CharityApp />} />
              {/* Public Store Routes (legacy) */}
              <Route path="/store" element={<StoreDirectory user={getUser()} onLogout={handleLogout} />} />
              <Route path="/store/:storeSlug/storefront" element={<StoreStorefront user={getUser()} onLogout={handleLogout} />} />
              {/* Retailer storefront: dealshaq.com/{slug} */}
              <Route path="/:slug" element={<RetailerStorefront />} />
            </Routes>
          )}
        </BrowserRouter>
        <LegalAcceptanceGate />
        <Toaster position="top-right" />
      </RTLWrapper>
    </LanguageProvider>
  );
}

export default App;
